import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFilter, isLoading } from './app/selectors';
import { AppActions } from './app/slice';
import { IEvent } from './app/types';
import GalleryCard from './components/GalleryCard';
import { useGalleryFilters } from './components/GalleryFilters';
import './GalleryPage.scss';

const GalleryPage: React.FC = () => {
  const dispatch = useDispatch();
  const loading = useSelector(isLoading);
  const filter = useSelector(getFilter);
  const { sortedEvents } = useGalleryFilters();

  useEffect(() => {
    if (!loading) return;
    dispatch(AppActions.startApp());
  }, [dispatch, loading]);

  // Listen for URL changes (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const categoryFromUrl = urlParams.get('category') || undefined;
      if (categoryFromUrl !== filter) {
        dispatch(AppActions.filterChanged(categoryFromUrl));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [dispatch, filter]);

  // Group events by category
  const groupedEvents = useMemo(() => {
    return sortedEvents.reduce(
      (acc, event) => {
        event.categories.forEach((category) => {
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(event);
        });
        return acc;
      },
      {} as Record<string, IEvent[]>
    );
  }, [sortedEvents]);

  // Add newest events at the top
  const finalGroupedEvents = useMemo(
    () => ({ Neueste: sortedEvents, ...groupedEvents }),
    [sortedEvents, groupedEvents]
  );

  const defaultItemsToShow = useMemo(() => {
    return Object.keys(finalGroupedEvents).reduce(
      (acc, category) => {
        acc[category] = category === 'Neueste' ? 3 : 6;
        return acc;
      },
      {} as { [category: string]: number }
    );
  }, [finalGroupedEvents]);

  const [itemsPerCategory, setItemsPerCategory] = React.useState<{ [category: string]: number }>(defaultItemsToShow);

  // Reset per-category item counts when filters or sort order change
  useEffect(() => {
    setItemsPerCategory(defaultItemsToShow);
  }, [defaultItemsToShow]);

  return (
    <div className="gallery-page" data-testid="gallery-page">
      {loading ? (
        <div className="gallery-loading" role="status" aria-live="polite">
          Laden...
        </div>
      ) : (
        <div className="gallery-content">
          {Object.entries(finalGroupedEvents).map(([category, events]) => {
            const itemsToShow = itemsPerCategory[category] || defaultItemsToShow[category] || 6;
            return (
              <section key={category} className="gallery-category">
                <h2 className="gallery-category__title">{category}</h2>
                <div className="gallery-category__grid">
                  {events.slice(0, itemsToShow).map((event) => (
                    <a key={event.id} href={event.pageURL} target="_top" aria-label={`Kurs ansehen: ${event.title}`}>
                      <GalleryCard
                        heading={event.title}
                        categories={event.categories}
                        authorName={event.presenterName}
                        authorTitle={event.presenterDescription}
                        authorImage={event.presenterImageURL}
                      />
                    </a>
                  ))}
                </div>
                {events.length > itemsToShow && (
                  <button
                    onClick={() =>
                      setItemsPerCategory((prev) => ({
                        ...prev,
                        [category]: (prev[category] || itemsToShow) + 3,
                      }))
                    }
                    className="gallery-category__show-more"
                    aria-label={`Mehr anzeigen ${category} Kurse`}
                  >
                    Mehr anzeigen
                  </button>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
